import { listQuestionnaires, Questionnaire, QuestionnaireVersion, QuestionAuthoringStatus, QuestionSet, QuestionSetVersion, Question, QuestionVersion, QuestionType, QuestionEvidenceRequirement, QuestionnaireEntry, QuestionnaireEntryType, QuestionSetEntry, createQuestion, createQuestionSet, createQuestionVersion, createQuestionSetVersion, createQuestionnaire, createQuestionnaireVersion, createQuestionnaireEntry, listQuestionSets, listQuestions, updateQuestionSet, updateQuestionVersion, updateQuestionSetVersion, createQuestionSetEntry, updateQuestionnaire, updateQuestionnaireVersion, getQuestion, getQuestionVersion, getQuestionSet, getQuestionSetVersion, getQuestionnaire, getQuestionnaireVersion, publishQuestionnaireVersion, publishQuestionVersion, publishQuestionSetVersion } from '../remote/questionnaires'

import { v4 as uuidv4, NIL } from 'uuid'

export const generateNewLocalId: () => string = () => {
	return `NEW:${uuidv4()}`
}

export const instantiateQuestionnaire: () => Questionnaire = () => {
	const questionnaireVersion = generateNewLocalId()
	return {
		id: questionnaireVersion,
		name: '',
		created: new Date(Date.now()),
		created_by: '',
		updated: new Date(Date.now()),
		updated_by: '',
		owned_by: '',
		has_draft: true,
		latest_version: '',
		versions: [instantiateQuestionnaireVersion(questionnaireVersion)]
	}
}

export const instantiateQuestionnaireVersion: (questionnaireId: string) => QuestionnaireVersion = (questionnaireId: string) => {
	return {
		id: generateNewLocalId(),
		comment: '',
		status: QuestionAuthoringStatus.DRAFT,
		questionnaire_id: questionnaireId,
		created: new Date(Date.now()),
		entries: [],
		created_by: '',
		updated: new Date(Date.now()),
		updated_by: '',
		version: undefined
	}
}

export const instantiateQuestionnaireEntry: (questionnaireVersionId: string, entryType: QuestionnaireEntryType) => QuestionnaireEntry = (questionnaireVersionId: string, entryType: QuestionnaireEntryType) => {
	return {
		id: generateNewLocalId(),
		questionnaire_id: questionnaireVersionId,
		entry_type: entryType
	}
}

export const instantiateQuestionSet: () => QuestionSet = () => {
	const questionSetVersion = generateNewLocalId()
	return {
		id: questionSetVersion,
		name: '',
		created: new Date(Date.now()),
		created_by: '',
		updated: new Date(Date.now()),
		updated_by: '',
		owned_by: '',
		has_draft: true,
		latest_version: '',
		versions: [instantiateQuestionSetVersion(questionSetVersion)]
	}
}

export const instantiateQuestionSetVersion: (questionSetId: string) => QuestionSetVersion = (questionSetId: string) => {
	return {
		id: generateNewLocalId(),
		comment: '',
		status: QuestionAuthoringStatus.DRAFT,
		question_set_id: questionSetId,
		created: new Date(Date.now()),
		entries: [],
		created_by: '',
		updated: new Date(Date.now()),
		updated_by: '',
		version: undefined
	}
}

export const instantiateQuestionSetVersionEntry: (questionSetVersionId: string, version: QuestionVersion) => QuestionSetEntry = (questionSetVersionId: string, version: QuestionVersion) => {
	return {
		id: generateNewLocalId(),
		question_set_id: questionSetVersionId,
		question: version
	}
}

export const instantiateQuestion: () => Question = () => {
	let questionId = generateNewLocalId()
	return {
		id: questionId,
		name: '',
		created: new Date(Date.now()),
		created_by: '',
		updated: new Date(Date.now()),
		updated_by: '',
		owned_by: '',
		has_draft: true,
		latest_version: '',
		versions: [instantiateQuestionVersion(questionId)]
	}
}

export const instantiateQuestionVersion: (questionId: string) => QuestionVersion = (questionId: string) => {
	return {
		id: generateNewLocalId(),
		comment: '',
		status: QuestionAuthoringStatus.DRAFT,
		question_id: questionId,
		created: new Date(Date.now()),
		created_by: '',
		updated: new Date(Date.now()),
		updated_by: '',
		version: undefined,
		text: '',
		question_type: QuestionType.YES_NO_PARTIAL,
		question_evidence_requirement: QuestionEvidenceRequirement.NEVER,
	}
}

export enum QuestionDataType {
	QUESTION,
	QUESTION_VERSION,
	QUESTION_SET,
	QUESTION_SET_VERSION,
	QUESTIONNAIRE,
	QUESTIONNAIRE_VERSION
}

export const MakeQuestionDataRef = (id: string, type: QuestionDataType): string => {
	return `${type}:${id}`
}

export interface QuestionnaireDataCache {
	// fill using MakeQuestionDataRef
	edits: Set<string>

	// map of NEW: IDs to backend IDs
	idMap: Record<string, string>

	questions: Record<string, Question>
	questionVersions: Record<string, QuestionVersion>
	questionSets: Record<string, QuestionSet>
	questionSetVersions: Record<string, QuestionSetVersion>
	questionnaires: Record<string, Questionnaire>
	questionnaireVersions: Record<string, QuestionnaireVersion>
}

export const getQuestionChildrenMissingFromCache = (cache: QuestionnaireDataCache, data: Question): string[] => {
	let results: string[] = []
	if ( ! cache.questions[data.id]) {
		results.push(MakeQuestionDataRef(data.id, QuestionDataType.QUESTION))
	}
	return results.concat(data.versions.filter((x) => ( ! cache.questionVersions[x.id])).map((x) => MakeQuestionDataRef(x.id, QuestionDataType.QUESTION_VERSION)))
}

export const getQuestionSetChildrenMissingFromCache = (cache: QuestionnaireDataCache, data: QuestionSet): string[] => {
	let results: string[] = []
	if ( ! cache.questionSets[data.id]) {
		results.push(MakeQuestionDataRef(data.id, QuestionDataType.QUESTION_SET))
	}

	return results.concat(
		data.versions.filter((x) => ( ! cache.questionSetVersions[x.id])).map((x) => MakeQuestionDataRef(x.id, QuestionDataType.QUESTION_SET_VERSION)),
		...data.versions.map((x) => getQuestionSetVersionChildrenMissingFromCache(cache, x))
	)
}

export const getQuestionSetVersionChildrenMissingFromCache = (cache: QuestionnaireDataCache, data: QuestionSetVersion): string[] => {
	let results: string[] = []
	if ( ! cache.questionSetVersions[data.id]) {
		results.push(MakeQuestionDataRef(data.id, QuestionDataType.QUESTION_SET_VERSION))
	}
	return results.concat(data.entries.filter((x) => ( ! cache.questions[x.question.question_id])).map((x) => MakeQuestionDataRef(x.question.question_id, QuestionDataType.QUESTION)))
}

export const getQuestionnaireChildrenMissingFromCache = (cache: QuestionnaireDataCache, data: Questionnaire): string[] => {
	let results: string[] = []
	if ( ! cache.questionnaires[data.id]) {
		results.push(MakeQuestionDataRef(data.id, QuestionDataType.QUESTIONNAIRE))
	}

	return results.concat(
		data.versions.filter((x) => (! cache.questionnaireVersions[x.id])).map((x) => MakeQuestionDataRef(x.id, QuestionDataType.QUESTIONNAIRE_VERSION)),
		...data.versions.map((x) => getQuestionnaireVersionChildrenMissingFromCache(cache, x))
	)
}

export const getQuestionnaireVersionChildrenMissingFromCache = (cache: QuestionnaireDataCache, data: QuestionnaireVersion): string[] => {
	let results: string[] = []
	if ( ! cache.questionnaireVersions[data.id]) {
		results.push(MakeQuestionDataRef(data.id, QuestionDataType.QUESTIONNAIRE_VERSION))
	}
	return results.concat(
		data.entries.filter((x) => x.question && ( ! cache.questions[x.question.question_id])).map((x) => MakeQuestionDataRef(x.question?.question_id ?? '', QuestionDataType.QUESTION)),
		data.entries.filter((x) => x.question_set && ( ! cache.questionSets[x.question_set.question_set_id])).map((x) => MakeQuestionDataRef(x.question_set?.question_set_id ?? '', QuestionDataType.QUESTION_SET)),
		...data.entries.filter((x) => x.question_set && ( ! cache.questionSets[x.question_set.question_set_id])).map((x) => x.question_set ? getQuestionSetVersionChildrenMissingFromCache(cache, x.question_set) : []),
	)
}

export interface UnpublishedChildren {
	questionVersions?: string[]
	questionSetVersions?: string[]
	questionnaireVersions?: string[]
}

export const getQuestionSetVersionUnpublishedChildren = (cache: QuestionnaireDataCache, questionSetVersion: string): UnpublishedChildren => {
	return {
		questionVersions: cache.questionSetVersions[questionSetVersion].entries
			.filter((x) => cache.questionVersions[x.question.id].status == QuestionAuthoringStatus.DRAFT)
			.map((x) => x.question.id),
		questionSetVersions: (cache.questionSetVersions[questionSetVersion].status == QuestionAuthoringStatus.DRAFT) ? [questionSetVersion] : undefined
	}
}

export const getQuestionnaireVersionUnpublishedChildren = (cache: QuestionnaireDataCache, questionnaireVersion: string): UnpublishedChildren => {
	return {
		questionVersions: cache.questionnaireVersions[questionnaireVersion].entries
			.filter((x) => x.question && cache.questionVersions[x.question.id].status == QuestionAuthoringStatus.DRAFT)
			.map((x) => x.question?.id ?? '')
			.concat(
				...cache.questionnaireVersions[questionnaireVersion].entries
				.filter((x) => x.question_set)
				.map((x) => getQuestionSetVersionUnpublishedChildren(cache, x.question_set?.id ?? '').questionVersions ?? [])
			),
		questionSetVersions: cache.questionnaireVersions[questionnaireVersion].entries
			.filter((x) => x.question_set && cache.questionSetVersions[x.question_set.id].status == QuestionAuthoringStatus.DRAFT)
			.map((x) => x.question_set?.id ?? ''),
		questionnaireVersions: (cache.questionnaireVersions[questionnaireVersion].status == QuestionAuthoringStatus.DRAFT) ? [questionnaireVersion] : undefined
	}
}

export const publishAll = (cache: QuestionnaireDataCache, targets: UnpublishedChildren, onFinish: (newCache: QuestionnaireDataCache) => void) => {
	let newCache = {...cache}

	Promise.all((targets.questionVersions ?? []).map((q) => {
		return new Promise<QuestionVersion>((resolve, reject) => {
			publishQuestionVersion({question: newCache.questionVersions[q].question_id, version: q}, undefined, (data, resp, err) => {
				if (resp?.status != 200) {
					reject(resp?.statusText)
				} else if (err) {
					reject(err.toJSON())
				} else if (data) {
					resolve(data)
				}
			})
		})
	})).then((versions) => {
		versions.forEach((version) => {
			newCache = {
				...newCache,
				questionVersions: {
					...newCache.questionVersions,
					[version.id]: version
				},
				questions: {
					...newCache.questions,
					[version.question_id]: {
						...newCache.questions[version.question_id],
						latest_version: version.id,
						versions: newCache.questions[version.question_id].versions.map((x) => x.id == version.id ? version : x)
					}
				}
			}
		})

		Promise.all((targets.questionSetVersions ?? []).map((q) => {
			return new Promise<QuestionSetVersion>((resolve, reject) => {
				publishQuestionSetVersion({questionSet: newCache.questionSetVersions[q].question_set_id, version: q}, undefined, (data, resp, err) => {
					if (resp?.status != 200) {
						reject(resp?.statusText)
					} else if (err) {
						reject(err.toJSON())
					} else if (data) {
						resolve(data)
					}
				})
			})
		})).then((versions) => {
			versions.forEach((version) => {
				newCache = {
					...newCache,
					questionSetVersions: {
						...newCache.questionSetVersions,
						[version.id]: version
					},
					questionSets: {
						...newCache.questionSets,
						[version.question_set_id]: {
							...newCache.questionSets[version.question_set_id],
							latest_version: version.id,
							versions: newCache.questionSets[version.question_set_id].versions.map((x) => x.id == version.id ? version : x)
						}
					}
				}
			})

			Promise.all((targets.questionnaireVersions ?? []).map((q) => {
				return new Promise<QuestionnaireVersion>((resolve, reject) => {
					publishQuestionnaireVersion({questionnaire: newCache.questionnaireVersions[q].questionnaire_id, version: q}, undefined, (data, resp, err) => {
						if (resp?.status != 200) {
							reject(resp?.statusText)
						} else if (err) {
							reject(err.toJSON())
						} else if (data) {
							resolve(data)
						}
					})
				})
			})).then((versions) => {
				versions.forEach((version) => {
					newCache = {
						...newCache,
						questionnaireVersions: {
							...newCache.questionnaireVersions,
							[version.id]: version
						},
						questionnaires: {
							...newCache.questionnaires,
							[version.questionnaire_id]: {
								...newCache.questionnaires[version.questionnaire_id],
								latest_version: version.id,
								versions: newCache.questionnaires[version.questionnaire_id].versions.map((x) => x.id == version.id ? version : x)
							}
						}
					}
				})

				onFinish(newCache)
			})
		})
	})
}

export const instantiateQuestionnaireCache = (): QuestionnaireDataCache => {
	return {
		edits: new Set<string>(),
		idMap: {} as Record<string, string>,
		questions: {},
		questionVersions: {},
		questionSets: {},
		questionSetVersions: {},
		questionnaires: {},
		questionnaireVersions: {},
	}
}

export const copyQuestionnaireCache = (source: QuestionnaireDataCache): QuestionnaireDataCache => {
	return {
		edits: new Set(...source.edits),
		idMap: {...source.idMap},
		questions: {...source.questions},
		questionVersions: {...source.questionVersions},
		questionSets: {...source.questionSets},
		questionSetVersions: {...source.questionSetVersions},
		questionnaires: {...source.questionnaires},
		questionnaireVersions: {...source.questionnaireVersions},
	}
}

export const retrieveQuestionData = (missing: string[], cache: QuestionnaireDataCache, onFinished: (newCache: QuestionnaireDataCache) => void) => {
	let cacheCopy = {...cache}
	Promise.all(missing.map((dataRef) => {
		return new Promise<void>((resolve, reject) => {
			const id = dataRef.split(':')[1]
			if (dataRef.startsWith(`${QuestionDataType.QUESTION}:`)) {
				getQuestion({question: id}, (data, resp, err) => {
					if (data) {
						cacheCopy = {...populateCacheWithQuestions(cacheCopy, [data])}
						resolve()
					} else {
						reject()
					}
				})
			} else if (dataRef.startsWith(`${QuestionDataType.QUESTION_VERSION}:`)) {
				console.log("Error! Should never happen.")
			} else if (dataRef.startsWith(`${QuestionDataType.QUESTION_SET}:`)) {
				getQuestionSet({questionSet: id}, (data, resp, err) => {
					if (data) {
						cacheCopy = {...populateCacheWithQuestionSets(cacheCopy, [data])}
						resolve()
					} else {
						reject()
					}
				})
			} else if (dataRef.startsWith(`${QuestionDataType.QUESTION_SET_VERSION}:`)) {
				console.log("Error! Should never happen.")
			} else if (dataRef.startsWith(`${QuestionDataType.QUESTIONNAIRE}:`)) {
				getQuestionnaire({questionnaire: id}, (data, resp, err) => {
					if (data) {
						cacheCopy = {...populateCacheWithQuestionnaires(cacheCopy, [data])}
						resolve()
					} else {
						reject()
					}
				})
			} else if (dataRef.startsWith(`${QuestionDataType.QUESTIONNAIRE_VERSION}:`)) {
				console.log("Error! Should never happen.")
			}
		})
	})).then(() => {
		onFinished(cacheCopy)
	})
}

export const populateCacheWithQuestions = (cache: QuestionnaireDataCache, questions: Question[]): QuestionnaireDataCache => {
	let questionRecords: Record<string, Question> = {}
	let versions: QuestionVersion[] = []
	questions.forEach((x) => {
		questionRecords[x.id] = x
		versions.push(...x.versions)
	})
	const newCache = populateCacheWithQuestionVersions(cache, versions)
	return {
		...newCache,
		questions: {...newCache.questions, ...questionRecords}
	}
}

export const populateCacheWithQuestionVersions = (cache: QuestionnaireDataCache, questionVersions: QuestionVersion[]): QuestionnaireDataCache => {
	let questionVersionRecords: Record<string, QuestionVersion> = {}
	questionVersions.forEach((x) => {
		questionVersionRecords[x.id] = x
	})
	return {
		...cache,
		questionVersions: {...cache.questionVersions, ...questionVersionRecords}
	}
}

export const populateCacheWithQuestionSets = (cache: QuestionnaireDataCache, questionSets: QuestionSet[]): QuestionnaireDataCache => {
	let questionSetRecords: Record<string, QuestionSet> = {}
	let versions: QuestionSetVersion[] = []
	questionSets.forEach((x) => {
		questionSetRecords[x.id] = x
		versions.push(...x.versions)
	})
	const newCache = populateCacheWithQuestionSetVersions(cache, versions)
	return {
		...newCache,
		questionSets: {...newCache.questionSets, ...questionSetRecords}
	}
}

export const populateCacheWithQuestionSetVersions = (cache: QuestionnaireDataCache, questionSetVersions: QuestionSetVersion[]): QuestionnaireDataCache => {
	let questionSetVersionRecords: Record<string, QuestionSetVersion> = {}
	let questionVersions: QuestionVersion[] = []
	questionSetVersions.forEach((x) => {
		questionSetVersionRecords[x.id] = x
		questionVersions.push(...x.entries.map((y) => y.question))
	})
	const newCache = populateCacheWithQuestionVersions(cache, questionVersions)
	return {
		...newCache,
		questionSetVersions: {...newCache.questionSetVersions, ...questionSetVersionRecords}
	}
}

export const populateCacheWithQuestionnaires = (cache: QuestionnaireDataCache, questionnaires: Questionnaire[]): QuestionnaireDataCache => {
	let questionnaireRecords: Record<string, Questionnaire> = {}
	let versions: QuestionnaireVersion[] = []
	questionnaires.forEach((x) => {
		questionnaireRecords[x.id] = x
		versions.push(...x.versions)
	})
	const newCache = populateCacheWithQuestionnaireVersions(cache, versions)
	return {
		...newCache,
		questionnaires: {...newCache.questionnaires, ...questionnaireRecords}
	}
}

export const populateCacheWithQuestionnaireVersions = (cache: QuestionnaireDataCache, questionnaireVersions: QuestionnaireVersion[]): QuestionnaireDataCache => {
	let questionSetVersionRecords: Record<string, QuestionnaireVersion> = {}
	let questionVersions: QuestionVersion[] = []
	let questionSetVersions: QuestionSetVersion[] = []
	questionnaireVersions.forEach((x) => {
		questionSetVersionRecords[x.id] = x
		questionVersions.push(...x.entries.map((y) => y.question).filter((z) => z != undefined))
		questionSetVersions.push(...x.entries.map((y) => y.question_set).filter((z) => z != undefined))
	})
	const newCache = populateCacheWithQuestionSetVersions(populateCacheWithQuestionVersions(cache, questionVersions), questionSetVersions)
	return {
		...newCache,
		questionnaireVersions: {...newCache.questionnaireVersions, ...questionSetVersionRecords}
	}
}

export enum SaveState {
	UNMODIFIED = 'UNMODIFIED',
	MODIFIED = 'MODIFIED',
	SAVING = 'SAVING',
	SAVED = 'SAVED',
	SAVED_MODIFIED = 'SAVED_MODIFIED',
	SAVE_FAILED = 'SAVE_FAILED',
}

export const saveQuestionnaireDataToBackend = (orgId: string, cache: QuestionnaireDataCache, saveState: SaveState, setSaveState: (s: SaveState) => void, setSaveMessage: (s: string) => void, onFinish: (newCache: QuestionnaireDataCache) => void) => {
	setSaveState(SaveState.SAVING)

	let cacheCopy = copyQuestionnaireCache(cache)

	const idMap = {} as Record<string, string>
	const editedQuestions = cache.edits.values().filter((x) => !x.includes(":NEW:") && x.startsWith(`${QuestionDataType.QUESTION}:`)).map((x) => cache.questions[x.split(':')[1]])
	const newQuestions = Object.values(cache.questions).filter((x) => x.id.startsWith("NEW:"))
	const editedQuestionVersions = cache.edits.values().filter((x) => !x.includes(":NEW:") && x.startsWith(`${QuestionDataType.QUESTION_VERSION}:`)).map((x) => cache.questionVersions[x.split(':')[1]])
	const newQuestionVersions = Object.values(cache.questionVersions).filter((x) => x.id.startsWith("NEW:"))
	const editedQuestionSets = cache.edits.values().filter((x) => !x.includes(":NEW:") && x.startsWith(`${QuestionDataType.QUESTION_SET}:`)).map((x) => cache.questionSets[x.split(':')[1]])
	const newQuestionSets = Object.values(cache.questionSets).filter((x) => x.id.startsWith("NEW:"))
	const editedQuestionSetVersions = cache.edits.values().filter((x) => !x.includes(":NEW:") && x.startsWith(`${QuestionDataType.QUESTION_SET_VERSION}:`)).map((x) => cache.questionSetVersions[x.split(':')[1]])
	const newQuestionSetVersions = Object.values(cache.questionSetVersions).filter((x) => x.id.startsWith("NEW:"))
	const editedQuestionnaires = cache.edits.values().filter((x) => !x.includes(":NEW:") && x.startsWith(`${QuestionDataType.QUESTIONNAIRE}:`)).map((x) => cache.questionnaires[x.split(':')[1]])
	const newQuestionnaires = Object.values(cache.questionnaires).filter((x) => x.id.startsWith("NEW:"))
	const editedQuestionnaireVersions = cache.edits.values().filter((x) => !x.includes(":NEW:") && x.startsWith(`${QuestionDataType.QUESTIONNAIRE_VERSION}:`)).map((x) => cache.questionnaireVersions[x.split(':')[1]])
	const newQuestionnaireVersions = Object.values(cache.questionnaireVersions).filter((x) => x.id.startsWith("NEW:"))

	Promise.all(newQuestions.map((source) => new Promise<Question>((resolve, reject) => {
		createQuestion({}, {owned_by: orgId}, (data, resp, err) => {
			if (err) {
				return reject(err.toJSON())
			} else if (resp?.status != 200) {
				return reject(resp?.statusText)
			} else if (data && data.id) {
				idMap[source.id] = data.id
				idMap[data.id] = source.id
				resolve(data)
			}
		})
	}))
	// there is no updateQuestion method yet
	// .concat(editedQuestions.map((source) => new Promise<Question>((resolve, reject) => {
	// 	// updateQuestion({} , {} )
	// })))
	).then((questions) => {
		cacheCopy.questions = {
			...cacheCopy.questions,
			...questions.reduce((acc, item) => (acc[item.id] = item, acc), {} as Record<string, Question>)
		}
		Promise.all(newQuestionSets.map((source) => new Promise<QuestionSet>((resolve, reject) => {
			createQuestionSet({}, {owned_by: orgId, name: source.name}, (data, resp, err) => {
				if (err) {
					return reject(err.toJSON())
				} else if (resp?.status != 200) {
					return reject(resp?.statusText)
				} else if (data && data.id) {
					idMap[source.id] = data.id;
					idMap[data.id] = source.id;
					resolve(data)
				}
			})
		})).concat([...editedQuestionSets].map((source) => new Promise<QuestionSet>((resolve, reject) => {
			updateQuestionSet({questionSet: source.id}, {name: source.name}, (data, resp, err) => {
				if (err) {
					return reject(err.toJSON())
				} else if (resp?.status != 200) {
					return reject(resp?.statusText)
				} else if (data && data.id) {
					resolve(data)
				}
			})
		})))).then((questionSets) => {
			cacheCopy.questionSets = {
				...cacheCopy.questionSets,
				...questionSets.reduce((acc, item) => (acc[item.id] = item, acc), {} as Record<string, QuestionSet>)
			}
			Promise.all(newQuestionVersions.map((source) => new Promise<QuestionVersion>((resolve, reject) => {
				const questionId = source.question_id.startsWith("NEW:") ? idMap[source.question_id] : source.question_id
				createQuestionVersion({question: questionId}, {
					comment: source.comment,
					question_evidence_requirement: source.question_evidence_requirement,
					question_type: source.question_type,
					status: source.status,
					text: source.text,
					question_id: questionId
				}, (data, resp, err) => {
					if (err) {
						return reject(err.toJSON())
					} else if (resp?.status != 200) {
						return reject(resp?.statusText)
					} else if (data && data.id) {
						idMap[source.id] = data.id;
						idMap[data.id] = source.id;
						cacheCopy.questions = {
							...cacheCopy.questions,
							[questionId]: {
								...cacheCopy.questions[questionId],
								versions: cacheCopy.questions[questionId].versions.filter((x) => x.id != source.id).concat([data])
							}
						}
						resolve(data)
					}
				})
			})).concat([...editedQuestionVersions].map((source => new Promise<QuestionVersion>((resolve, reject) => {
				const questionId = source.question_id.startsWith("NEW:") ? idMap[source.question_id] : source.question_id
				updateQuestionVersion({question: questionId, version: source.id}, {
					comment: source.comment,
					question_evidence_requirement: source.question_evidence_requirement,
					question_type: source.question_type,
					text: source.text,
				}, (data, resp, err) => {
					if (err) {
						return reject(err.toJSON())
					} else if (resp?.status != 200) {
						return reject(resp?.statusText)
					} else if (data && data.id) {
						idMap[source.id] = data.id;
						idMap[data.id] = source.id;
						resolve(data)
					}
				})
			}))))).then((questionVersions) => {
				cacheCopy.questionVersions = {
					...cacheCopy.questionVersions,
					...questionVersions.reduce((acc, item) => (acc[item.id] = item, acc), {} as Record<string, QuestionVersion>)
				}
				Promise.all(newQuestionSetVersions.map((source) => new Promise<QuestionSetVersion>((resolve, reject) => {
					const questionSetId = source.question_set_id.startsWith("NEW:") ? idMap[source.question_set_id] : source.question_set_id
					createQuestionSetVersion({questionSet: questionSetId}, {
						comment: source.comment,
						question_set_id: questionSetId,
						entries: source.entries.map((x) => {
							return {
								question_set_id: NIL,
								question: (x.question.id.startsWith("NEW:") ? idMap[x.question.id] : x.question.id)
							}
						})
					}, (data, resp, err) => {
						if (err) {
							return reject(err.toJSON())
						} else if (resp?.status != 200) {
							return reject(resp?.statusText)
						} else if (data && data.id) {
							idMap[source.id] = data.id;
							idMap[data.id] = source.id;
							cacheCopy.questionSets = {
								...cacheCopy.questionSets,
								[questionSetId]: {
									...cacheCopy.questionSets[questionSetId],
									versions: cacheCopy.questionSets[questionSetId].versions.filter((x) => x.id != source.id).concat([data])
								}
							}
							resolve(data)
						}
					})
				})).concat([...editedQuestionSetVersions].map((source) => new Promise<QuestionSetVersion>((resolve, reject) => {
					const questionSetId = source.question_set_id.startsWith("NEW:") ? idMap[source.question_set_id] : source.question_set_id
					const newEntries = source.entries.filter((x) => x.id.startsWith("NEW:"))
					updateQuestionSetVersion({questionSet: questionSetId, version: source.id}, {
						comment: source.comment
					}, (data, resp, err) => {
						if (err) {
							return reject(err.toJSON())
						} else if (resp?.status != 200) {
							return reject(resp?.statusText)
						} else if (data && data.id) {
							idMap[source.id] = data.id;
							idMap[data.id] = source.id;
							Promise.all(newEntries.map((newEntry) => new Promise<QuestionSetEntry>((entryResolve, entryReject) => {
								const questionId = newEntry.question.id.startsWith("NEW:") ? idMap[newEntry.question.id] : newEntry.question.id
								createQuestionSetEntry({questionSet: questionSetId, version: data.id}, {question: questionId, question_set_id: data.id}, (entryData, resp, err) => {
									if (err) {
										return entryReject(err.toJSON())
									} else if (resp?.status != 200) {
										return entryReject(resp?.statusText)
									} else if (entryData) {
										idMap[newEntry.id] = entryData.id
										idMap[entryData.id] = newEntry.id
										cacheCopy.questionSetVersions = {
											...cacheCopy.questionSetVersions,
											[data.id]: {
												...cacheCopy.questionSetVersions[data.id],
												entries: cacheCopy.questionSetVersions[data.id].entries.filter((x) => x.id != newEntry.id).concat([entryData])
											}
										}
										entryResolve(entryData)
									}
								})
							}))).then(() => {
								resolve(data)
							}, (reason) => {
								reject(reason)
							})
						}
					})
				})))).then((questionSetVersions) => {
					cacheCopy.questionSetVersions = {
						...cacheCopy.questionSetVersions,
						...questionSetVersions.reduce((acc, item) => (acc[item.id] = item, acc), {} as Record<string, QuestionSetVersion>)
					}
					Promise.all(newQuestionnaires.map((source) => new Promise<Questionnaire>((resolve, reject) => {
						createQuestionnaire({}, {name: source.name, owned_by: orgId}, (data, resp, err) => {
							if (err) {
								return reject(err.toJSON())
							} else if (resp?.status != 200) {
								return reject(resp?.statusText)
							} else if (data) {
								idMap[data.id] = source.id
								idMap[source.id] = data.id
								resolve(data)
							}
						})
					})).concat([...editedQuestionnaires].map((source) => new Promise<Questionnaire>((resolve, reject) => {
						updateQuestionnaire({questionnaire: source.id}, {name: source.name}, (data, resp, err) => {
							if (err) {
								return reject(err.toJSON())
							} else if (resp?.status != 200) {
								return reject(resp?.statusText)
							} else if (data) {
								idMap[data.id] = source.id
								idMap[source.id] = data.id
								resolve(data)
							}
						})
					})))).then((questionnaires) => {
						cacheCopy.questionnaires = {
							...cacheCopy.questionnaires,
							...questionnaires.reduce((acc, item) => (acc[item.id] = item, acc), {} as Record<string, Questionnaire>)
						}
						Promise.all(newQuestionnaireVersions.map((source) => new Promise<QuestionnaireVersion>((resolve, reject) => {
							const questionnaireId = source.questionnaire_id.startsWith("NEW:") ? idMap[source.questionnaire_id] : source.questionnaire_id
							createQuestionnaireVersion({questionnaire: questionnaireId}, {
								questionnaire_id: questionnaireId,
								comment: source.comment,
								entries: source.entries.map((x) => {return {
									questionnaire_id: NIL,
									entry_type: x.entry_type,
									question: (x.question?.id.startsWith("NEW:") ? idMap[x.question.id] : x.question?.id) ?? undefined,
									question_set: (x.question_set?.id.startsWith("NEW:") ? idMap[x.question_set.id] : x.question_set?.id) ?? undefined
								}})
							}, (data, resp, err) => {
								if (err) {
									return reject(err.toJSON())
								} else if (resp?.status != 200) {
									return reject(resp?.statusText)
								} else if (data) {
									idMap[data.id] = source.id
									idMap[source.id] = data.id
									cacheCopy.questionnaires = {
										...cacheCopy.questionnaires,
										[questionnaireId]: {
											...cacheCopy.questionnaires[questionnaireId],
											versions: cacheCopy.questionnaires[questionnaireId].versions.filter((x) => x.id != source.id).concat([data])
										}
									}
									resolve(data)
								}
							})
						})).concat([...editedQuestionnaireVersions].map((source) => new Promise<QuestionnaireVersion>((resolve, reject) => {
							const questionnaireId = source.questionnaire_id.startsWith("NEW:") ? idMap[source.questionnaire_id] : source.questionnaire_id
							const newEntries = source.entries.filter((x) => x.id.startsWith("NEW:"))
							updateQuestionnaireVersion({questionnaire: questionnaireId, version: source.id}, {
								comment: source.comment,
							}, (data, resp, err) => {
								if (err) {
									return reject(err.toJSON())
								} else if (resp?.status != 200) {
									return reject(resp?.statusText)
								} else if (data) {
									idMap[data.id] = source.id
									idMap[source.id] = data.id

									Promise.all(newEntries.map((newEntry) => new Promise<QuestionnaireEntry>((entryResolve, entryReject) => {
										createQuestionnaireEntry({questionnaire: data.questionnaire_id, version: data.id}, {
											questionnaire_id: data.id,
											entry_type: newEntry.entry_type,
											question: (newEntry.question?.id.startsWith("NEW:") ? idMap[newEntry.question.id] : newEntry.question?.id) ?? undefined,
											question_set: (newEntry.question_set?.id.startsWith("NEW:") ? idMap[newEntry.question_set.id] : newEntry.question_set?.id) ?? undefined
										}, (data, resp, err) => {
											if (err) {
												return reject(err.toJSON())
											} else if (resp?.status != 200) {
												return reject(resp?.statusText)
											} else if (data) {
												idMap[newEntry.id] = data.id
												idMap[data.id] = newEntry.id
											}
										})
									}))).then(() => {
										resolve(data)
									})
									resolve(data)
								}
							})
						})))).then((questionnaireVersions) => {
							cacheCopy.questionnaireVersions = {
								...cacheCopy.questionnaireVersions,
								...questionnaireVersions.reduce((acc, item) => (acc[item.id] = item, acc), {} as Record<string, QuestionnaireVersion>)
							}

							cacheCopy.idMap = {...cacheCopy.idMap, ...idMap}

							cacheCopy.edits = new Set<string>()

							cacheCopy.questions = Object.values(cacheCopy.questions).filter((q) => !q.id.startsWith("NEW")).reduce((acc, curr) => {
								return {
									...acc,
									[curr.id]: curr
								}
							}, {} as Record<string, Question>)
							cacheCopy.questionVersions = Object.values(cacheCopy.questionVersions).filter((q) => !q.id.startsWith("NEW")).reduce((acc, curr) => {
								return {
									...acc,
									[curr.id]: curr
								}
							}, {} as Record<string, QuestionVersion>)
							cacheCopy.questionSets = Object.values(cacheCopy.questionSets).filter((q) => !q.id.startsWith("NEW")).reduce((acc, curr) => {
								return {
									...acc,
									[curr.id]: curr
								}
							}, {} as Record<string, QuestionSet>)
							cacheCopy.questionSetVersions = Object.values(cacheCopy.questionSetVersions).filter((q) => !q.id.startsWith("NEW")).reduce((acc, curr) => {
								return {
									...acc,
									[curr.id]: curr
								}
							}, {} as Record<string, QuestionSetVersion>)
							cacheCopy.questionnaires = Object.values(cacheCopy.questionnaires).filter((q) => !q.id.startsWith("NEW")).reduce((acc, curr) => {
								return {
									...acc,
									[curr.id]: curr
								}
							}, {} as Record<string, Questionnaire>)
							cacheCopy.questionnaireVersions = Object.values(cacheCopy.questionnaireVersions).filter((q) => !q.id.startsWith("NEW")).reduce((acc, curr) => {
								return {
									...acc,
									[curr.id]: curr
								}
							}, {} as Record<string, QuestionnaireVersion>)

							onFinish(cacheCopy)

							if (saveState == 'MODIFIED') {
								setSaveState(SaveState.SAVED_MODIFIED)
							} else {
								setSaveState(SaveState.SAVED)
							}
						}, (reason) => {
							setSaveState(SaveState.SAVE_FAILED)
							setSaveMessage(reason)
						})
					}, (reason) => {
						setSaveState(SaveState.SAVE_FAILED)
						setSaveMessage(reason)
					})
				}, (reason) => {
					setSaveState(SaveState.SAVE_FAILED)
					setSaveMessage(reason)
				})
			}, (reason) => {
				setSaveState(SaveState.SAVE_FAILED)
				setSaveMessage(reason)
			})
		}, (reason) => {
			setSaveState(SaveState.SAVE_FAILED)
			setSaveMessage(reason)
		})
	}, (reason) => {
		setSaveState(SaveState.SAVE_FAILED)
		setSaveMessage(reason)
	})
}
